// Members in the project document (W9c; PROTOCOL.md §10.13, design-docs/W9 — Shared projects (scope).md §4.3): who
// writes in a shared project, by account, with the Automerge actors and word-log devices each one writes with, so a
// change (or a comment, a checkpoint, a character of text) can be put down to a person.
//
//   members: { <accountId>: { name: str, color: str ("r g b", `EntityColors`), actors: [str], devices: [str], joinedAt: timestamp } }
//
// Each device adds its own actor (and device id) to its own account's entry on its first write to a shared project, so
// every member writes only their own key. Two things can still meet: two members making the root `members` map at
// once, or two devices of one member making their entry (or its lists) at once. Automerge keeps both objects as a
// conflict at that key and shows one; readers here (and the Swift's) read every conflicting value (`getAll`) and join
// them, so nothing a device recorded is lost, and writers never replace an object that's there.
import * as A from '@automerge/automerge';
import { nextEntityColor } from '../colors.ts';
import { objectsAt } from '../automerge/joined.ts';
import { ROOT, SwiftReader, type ObjID } from '../automerge/swiftDoc.ts';
import { timestamp, str, Writer } from '../edit/typed.ts';

export const MEMBERS_KEY = 'members';

/** One member as the project document records them (every device's writes joined). */
export interface Member {
  /** The relay account id. */
  id: string;
  name: string;
  /** "r g b" (0…1), as entity colours are stored. */
  color: string;
  /** Hex Automerge actor ids this member's devices write with, oldest first. */
  actors: string[];
  /** Word-log device ids (`"<day> <device> +|-"`). */
  devices: string[];
  joinedAt: Date | null;
}

/** Who wrote something: a member, or a device no member has claimed. */
export type WrittenBy = { kind: 'member'; member: Member } | { kind: 'unknown' };
export const UNKNOWN_DEVICE = 'Unknown device';
/** The colour unclaimed writing is shown in (a neutral grey). */
export const UNKNOWN_COLOR = '0.55 0.55 0.55';

/** The members of a project as one lookup: by id, by actor, by device. */
export interface Members {
  list: Member[];
  byId: Map<string, Member>;
  byActor: Map<string, Member>;
  byDevice: Map<string, Member>;
}

const emptyMembers = (): Members => ({ list: [], byId: new Map(), byActor: new Map(), byDevice: new Map() });

/** Every scalar value stored at `key`, winner first. */
function scalarsAt(r: SwiftReader, obj: ObjID, key: string): [string, unknown][] {
  const out: [string, unknown][] = [];
  const winner = r.get(obj, key);
  if (winner) out.push(winner);
  for (const v of (r.backend.getAll(obj, key, r.heads) as unknown as [string, unknown, string][]) ?? []) {
    if (!out.some((o) => o[0] === v[0] && sameValue(o[1], v[1]))) out.push([v[0], v[1]]);
  }
  return out;
}

const sameValue = (a: unknown, b: unknown) => (a instanceof Date && b instanceof Date ? a.getTime() === b.getTime() : a === b);

/** The strings in every list stored at `key`, in order, each once. */
function stringsAt(r: SwiftReader, obj: ObjID, key: string, into: string[]) {
  for (const list of objectsAt(r, obj, key, 'list')) {
    for (const [t, v] of r.values(list)) if (t === 'str' && typeof v === 'string' && v !== '' && !into.includes(v)) into.push(v);
  }
}

/** `members` from a project document (at `heads`, else now). Missing or unreadable parts read as nothing. */
export function readMembers(doc: A.Doc<unknown>, heads?: string[]): Members {
  const r = new SwiftReader(doc, heads);
  const out = emptyMembers();
  let roots: ObjID[];
  try {
    roots = objectsAt(r, ROOT, MEMBERS_KEY, 'map');
  } catch {
    return out;
  }
  const byId = new Map<string, Member>();
  for (const root of roots) {
    for (const id of r.keys(root)) {
      for (const entry of objectsAt(r, root, id, 'map')) {
        const m = byId.get(id) ?? { id, name: '', color: '', actors: [], devices: [], joinedAt: null };
        for (const [t, v] of scalarsAt(r, entry, 'name')) if (!m.name && t === 'str' && typeof v === 'string') m.name = v;
        for (const [t, v] of scalarsAt(r, entry, 'color')) if (!m.color && t === 'str' && typeof v === 'string') m.color = v;
        for (const [t, v] of scalarsAt(r, entry, 'joinedAt')) {
          if (t === 'timestamp' && v instanceof Date && (!m.joinedAt || v < m.joinedAt)) m.joinedAt = v;
        }
        stringsAt(r, entry, 'actors', m.actors);
        stringsAt(r, entry, 'devices', m.devices);
        byId.set(id, m);
      }
    }
  }
  // Oldest member first (the owner, who records first), then by id.
  out.list = [...byId.values()].sort((a, b) => (a.joinedAt?.getTime() ?? Infinity) - (b.joinedAt?.getTime() ?? Infinity) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (const m of out.list) {
    if (!m.name) m.name = 'Someone';
    out.byId.set(m.id, m);
    // An actor claimed twice (it can't be, unless a client is broken) belongs to whoever claimed it first.
    for (const a of m.actors) if (!out.byActor.has(a)) out.byActor.set(a, m);
    for (const d of m.devices) if (!out.byDevice.has(d)) out.byDevice.set(d, m);
  }
  return out;
}

/** Whether the project document has a `members` map at all (a project that has been shared). */
export function hasMembers(doc: A.Doc<unknown>): boolean {
  const v = new SwiftReader(doc).get(ROOT, MEMBERS_KEY);
  return !!v && v[0] === 'map';
}

// MARK: Who wrote it

/** The actor of an op or object id ("12@abcd…" → "abcd…"); a bare actor is returned as it is. */
export function actorOf(id: string): string {
  const at = id.indexOf('@');
  return at < 0 ? id : id.slice(at + 1);
}

/** `whoWrote(change)`: the member whose devices write with the change's actor (a change's metadata, an op or object
 *  id, or an actor), else an unknown device. */
export function whoWrote(members: Members, change: string | { actor: string }): WrittenBy {
  const actor = typeof change === 'string' ? actorOf(change) : change.actor;
  const member = members.byActor.get(actor);
  return member ? { kind: 'member', member } : { kind: 'unknown' };
}

/** The name to show for a writer ("Unknown device" when no member has claimed it). */
export const writerName = (w: WrittenBy) => (w.kind === 'member' ? w.member.name : UNKNOWN_DEVICE);
/** The colour to show for a writer ("r g b"). */
export const writerColor = (w: WrittenBy) => (w.kind === 'member' ? w.member.color || UNKNOWN_COLOR : UNKNOWN_COLOR);

/** Who made the object at a map key (a comment, a suggestion, a checkpoint: the change that made its map). Null when
 *  there's no object there. */
export function whoMadeObject(members: Members, doc: A.Doc<unknown>, path: string[]): WrittenBy | null {
  const r = new SwiftReader(doc);
  let obj: ObjID = ROOT;
  for (const key of path) {
    const v = r.get(obj, key);
    if (!v || (v[0] !== 'map' && v[0] !== 'list' && v[0] !== 'text')) return null;
    obj = v[1] as ObjID;
  }
  return whoWrote(members, obj);
}

/** The member a word-log device belongs to, else null ("Unknown"). */
export const memberForDevice = (members: Members, device: string): Member | null => members.byDevice.get(device) ?? null;

// MARK: Recording this device

export interface MemberSelf {
  /** This account's relay id. */
  id: string;
  /** The account's name as it should show. */
  name: string;
  /** The actor this device writes with now. */
  actors: string[];
  /** This device's word-log id. */
  device?: string;
  now: Date;
}

/** What `recordMember` would add: nothing when this device's actors and device are recorded and the name is current. */
export function memberNeedsRecording(members: Members, self: Omit<MemberSelf, 'now'>): boolean {
  const m = members.byId.get(self.id);
  if (!m) return true;
  if (self.actors.some((a) => !m.actors.includes(a))) return true;
  if (self.device && !m.devices.includes(self.device)) return true;
  return self.name.trim() !== '' && m.name !== self.name.trim();
}

/**
 * Records this device in its member's entry, inside a change to the project document (`w`/`d` over it, `r` the reader
 * of the document as the change began): the entry (name, colour, joinedAt) made if missing, the name kept current, and
 * any of `actors` and `device` not yet listed appended. Never replaces an object that's there. Returns whether it wrote.
 */
export function recordMember(w: Writer, d: Record<string, any>, r: SwiftReader, self: MemberSelf): boolean {
  const members = readMembers(r.doc, r.heads);
  if (!memberNeedsRecording(members, self)) return false;
  const known = members.byId.get(self.id);
  const name = self.name.trim() || known?.name || 'Someone';
  if (r.get(ROOT, MEMBERS_KEY)?.[0] !== 'map') d[MEMBERS_KEY] = {};
  const root = d[MEMBERS_KEY];
  // This change's view: the entry the proxy shows (the winner), made if missing.
  const rootObj = r.map(ROOT, MEMBERS_KEY);
  const entryObj = rootObj ? r.get(rootObj, self.id) : null;
  const path = [MEMBERS_KEY, self.id];
  if (!entryObj || entryObj[0] !== 'map') {
    root[self.id] = {};
    w.put(path, 'name', str(name));
    w.put(path, 'color', str(known?.color || nextEntityColor('character', members.list.map((m) => ({ kind: 'character' as const, color: m.color || undefined })))));
    w.put(path, 'joinedAt', timestamp(known?.joinedAt ?? self.now));
  } else if ((r.string(entryObj[1] as ObjID, 'name') ?? '') !== name) {
    w.put(path, 'name', str(name));
  }
  const entry = root[self.id];
  const listed = (key: 'actors' | 'devices', values: string[]) => {
    const have = known?.[key] ?? [];
    const missing = values.filter((v, i) => v !== '' && !have.includes(v) && values.indexOf(v) === i);
    if (!missing.length) return;
    const stored = entryObj && entryObj[0] === 'map' ? r.get(entryObj[1] as ObjID, key) : null;
    if (!stored || stored[0] !== 'list') entry[key] = [];
    const list = entry[key] as unknown[];
    // Strings go in as scalars (a plain string would make a text object).
    for (const v of missing) list.push(new A.ImmutableString(v));
  };
  listed('actors', self.actors);
  listed('devices', self.device ? [self.device] : []);
  return true;
}

/** `recordMember` as one change to a project document (tests, the CLI): the document after, or the same one when nothing
 *  needed recording. */
export function recordMemberIn(doc: A.Doc<unknown>, self: MemberSelf, message?: string): A.Doc<unknown> {
  const r = new SwiftReader(doc);
  if (!memberNeedsRecording(readMembers(doc), self)) return doc;
  const opts = { time: Math.floor(self.now.getTime() / 1000), ...(message ? { message } : {}) };
  return A.change(doc as A.Doc<Record<string, any>>, opts, (d) => {
    recordMember(new Writer(d, r), d, r, self);
  }) as A.Doc<unknown>;
}
